import { mat4, quat, vec2, vec3 } from 'gl-matrix'

import Texture from '../textures/Texture'
import Color from '../math/Color'

export default class RenderTarget {
  constructor(gl, width, height, options = {}) {
    this._gl = gl;
    this._width = Math.ceil(width);
    this._height = Math.ceil(height);
    this._isBindedOnce = false;

    this._bgColor = new Color(0, 0, 0);
    this._bgAlpha = 1;

    this.autoClear = options.autoClear || true;

    this.textureUnitIndex = Texture.textureCnt++;

    this.wrapS = options.wrapS || 'CLAMP_TO_EDGE';
    this.wrapT = options.wrapT || 'CLAMP_TO_EDGE';
    this.magFilter = options.magFilter || 'LINEAR';
    this.minFilter = options.minFilter || 'LINEAR';
    this.mipmap = false;

    this._format = options.format || 'RGBA';
    this._type = options.type || 'UNSIGNED_BYTE';

    let ext;
    if(this._type == 'FLOAT') {
      if(!this._gl.getExtension('OES_texture_float')) {
        throw new Error('no support float texture');
      }
      this.type = this._gl[this._type];

    } else if(this._type == 'HALF_FLOAT_OES') {
      ext = this._gl.getExtension('OES_texture_half_float');
      if(ext) {
        this.type = ext.HALF_FLOAT_OES;
      } else {
        throw new Error('no support half float texture');
      }
    } else {
      this.type = this._gl[this._type];
    }

    this.format = this._gl[this._format];

    this._createFrameBuffer();

    this._isDisposed = false;
  }

  setBgColor(color, alpha = 1) {
    this._bgColor = color;
    this._bgAlpha = alpha;
  }

  get texture() {
    return this;
  }

  set width(value) {
    this._width = value;
    this.setSize(this._width, this._height);
  }
  set height(value) {
    this._height = value;
    this.setSize(this._width, this._height);
  }
  get width() {
    return this._width;
  }
  get height() {
    return this._height;
  }

  get isDesposed() {
    return this._isDisposed;
  }

  dispose() {
    this._deleteFrameBuffer();
    this._gl = null;
    this._isDisposed = true;
  }

  updateFrameBuffer() {
    this._deleteFrameBuffer();
    this._createFrameBuffer();
  }

  getGLTexture() {
    return this._glTexture;
  }

  onBeforeRender() {
    this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, this._frameBuffer);
    this._gl.viewport(0, 0, this._textureWidth, this._textureHeight);
    if(this.autoClear) this.clear();
  }

  onAfterRender() {
    this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, null);
  }

  clear() {
    this._gl.clearColor(this._bgColor.r, this._bgColor.g, this._bgColor.b, this._bgAlpha);
    this._gl.clearDepth(1);
    this._gl.clear(this._gl.COLOR_BUFFER_BIT | this._gl.DEPTH_BUFFER_BIT);
  }

  bind(uniformLocation) {
    // テクスチャが複数ある場合におかしくなる
    // if(!this._glTexture || uniformLocation == -1 || this._isBindedOnce) return;

    if(uniformLocation == -1 || !this._glTexture) return;

    this._gl.activeTexture(this._gl['TEXTURE' + this.textureUnitIndex.toString()]);
    this._gl.bindTexture(this._gl.TEXTURE_2D, this._glTexture);
    this._gl.uniform1i(uniformLocation, this.textureUnitIndex);
    this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_WRAP_S, this._gl[this.wrapS]);
    this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_WRAP_T, this._gl[this.wrapT]);
    this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_MAG_FILTER, this._gl[this.magFilter]);
    this._gl.texParameteri(this._gl.TEXTURE_2D, this._gl.TEXTURE_MIN_FILTER, this._gl[this.minFilter]);
  }

  setSize(width, height) {
    this._width = Math.ceil(width);
    this._height = Math.ceil(height);
    this.updateFrameBuffer();
  }

  _createFrameBuffer() {
    this._textureWidth = this._getTextureSize(this._width);
    this._textureHeight = this._getTextureSize(this._height);

    // frameBuffer
    this._frameBuffer = this._gl.createFramebuffer();
    this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, this._frameBuffer);

    // depth
    this._depthRenderBuffer = this._gl.createRenderbuffer();
    this._gl.bindRenderbuffer(this._gl.RENDERBUFFER, this._depthRenderBuffer);
    this._gl.renderbufferStorage(this._gl.RENDERBUFFER, this._gl.DEPTH_COMPONENT16, this._textureWidth, this._textureHeight);
    this._gl.framebufferRenderbuffer(this._gl.FRAMEBUFFER, this._gl.DEPTH_ATTACHMENT, this._gl.RENDERBUFFER, this._depthRenderBuffer);

    // texture
    this._glTexture = this._gl.createTexture();
    this._gl.bindTexture(this._gl.TEXTURE_2D, this._glTexture);
    this._gl.texImage2D(this._gl.TEXTURE_2D, 0, this.format, this._textureWidth, this._textureHeight, 0, this.format, this.type, null);
    if(this.mipmap) this._gl.generateMipmap(this._gl.TEXTURE_2D);
    this._gl.framebufferTexture2D(this._gl.FRAMEBUFFER, this._gl.COLOR_ATTACHMENT0, this._gl.TEXTURE_2D, this._glTexture, 0);

    this._gl.bindTexture(this._gl.TEXTURE_2D, null);
    this._gl.bindRenderbuffer(this._gl.RENDERBUFFER, null);
    this._gl.bindFramebuffer(this._gl.FRAMEBUFFER, null);
  }

  _deleteFrameBuffer() {
    if(this._frameBuffer) {
      this._gl.deleteFramebuffer(this._frameBuffer);
      this._frameBuffer = null;
    }
    if(this._glTexture) {
      this._gl.deleteTexture(this._glTexture);
      this._glTexture = null;
    }
    if(this._depthRenderBuffer) {
      this._gl.deleteRenderbuffer(this._depthRenderBuffer);
      this._depthRenderBuffer = null;
    }
  }

  _getTextureSize(value) {
    const a = value.toString(2).length;
    const n1 = Math.pow(2, a);
    const n2 = Math.pow(2, a - 1);
    if(n1 - value > value - n2) {
      return Math.max(2, n2);
    } else {
      return Math.max(2, n2);
    }
  }

  getTextureResolution() {
    return {
      x: this._textureWidth,
      y: this._textureHeight
    }
  }
}
